import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { personalInfo } from "../data/mock";

/**
 * Pure Three.js implementation of the avatar viewer.
 * Avoids react-three/fiber JSX issues with CRA dev-mode source props.
 */
const Avatar3D = () => {
  const mountRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 100);
    camera.position.set(0, 0.2, 3.4);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    mount.appendChild(renderer.domElement);

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.55);
    scene.add(ambient);

    const keyLight = new THREE.DirectionalLight(0xa5f3fc, 1.4);
    keyLight.position.set(3, 4, 3);
    scene.add(keyLight);

    const rimLight = new THREE.DirectionalLight(0xfbbf24, 0.7);
    rimLight.position.set(-3, 2, -2);
    scene.add(rimLight);

    const fillLight = new THREE.PointLight(0x22d3ee, 0.6, 8);
    fillLight.position.set(0, 1, 2);
    scene.add(fillLight);

    // Avatar group
    const avatarGroup = new THREE.Group();
    scene.add(avatarGroup);

    // Load model
    const loader = new GLTFLoader();
    let model = null;
    loader.load(
      personalInfo.avatarUrl,
      (gltf) => {
        model = gltf.scene;
        // Center & scale
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2.2 / maxDim;
        model.scale.setScalar(scale);
        model.position.x = -center.x * scale;
        model.position.y = -center.y * scale - 0.1;
        model.position.z = -center.z * scale;

        // Improve materials
        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = false;
            child.receiveShadow = false;
            if (child.material) {
              child.material.envMapIntensity = 0.9;
            }
          }
        });

        avatarGroup.add(model);
        setLoaded(true);
      },
      undefined,
      (err) => {
        console.error("GLB load error:", err);
        setError("Failed to load avatar");
      }
    );

    // Animation
    const clock = new THREE.Clock();
    let frameId;
    const animate = () => {
      const t = clock.getElapsedTime();
      avatarGroup.rotation.y = t * 0.35;
      avatarGroup.position.y = Math.sin(t * 1.2) * 0.05;
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    // Resize
    const onResize = () => {
      if (!mount) return;
      width = mount.clientWidth;
      height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener("resize", onResize);

    // Cleanup
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", onResize);
      if (model) {
        model.traverse((c) => {
          if (c.isMesh) {
            c.geometry?.dispose();
            if (Array.isArray(c.material)) {
              c.material.forEach((m) => m.dispose());
            } else {
              c.material?.dispose();
            }
          }
        });
      }
      renderer.dispose();
      if (mount && renderer.domElement && mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div className="relative w-full h-full">
      {/* Background glow */}
      <div className="absolute inset-x-8 bottom-10 h-24 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-cyan-500/5 via-transparent to-transparent pointer-events-none" />

      <div ref={mountRef} className="absolute inset-0" />

      {!loaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-cyan-400/30 border-t-cyan-300 animate-spin" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300/70">
              Loading avatar
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <p className="font-mono text-xs text-zinc-500">{error}</p>
        </div>
      )}
    </div>
  );
};

export default Avatar3D;
